import * as Popover from '@radix-ui/react-popover';
import { Avatar } from './primitives';

/**
 * How the interface names people and agents (FRONTEND-AUDIT B8, §D).
 *
 * One place, because the twin's label was typed three times and was wrong
 * in all three: "Intars Olbiks's twin". The decision is the first name and
 * an English possessive — "Sol · Iryna's twin", "Fen · Intars' twin".
 */

/** The first name of a display name: "Intars Olbiks" → "Intars". */
export function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName.trim();
}

/** "Iryna's", "Intars'": an apostrophe alone after an s. */
export function possessive(name: string): string {
  const n = name.trim();
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
}

/** "Iryna's twin", from the principal's display name. */
export function twinOf(principalDisplayName: string): string {
  return `${possessive(firstName(principalDisplayName))} twin`;
}

/** "Sol · Iryna's twin": the twin's own name, then whose it is. */
export function twinLabel(twinGivenName: string, principalDisplayName: string): string {
  return `${twinGivenName} · ${twinOf(principalDisplayName)}`;
}

/** What a counterparty is to us, as the chip the directory shows (ADR-0063). */
export function relationshipChip(r: string): { label: string; tone: 'warning' | 'accent' } {
  return r === 'unapproved' ? { label: 'UNAPPROVED', tone: 'warning' } : { label: r.toUpperCase(), tone: 'accent' };
}

/**
 * An agent or a twin, and what it is, on hover or on a tap (FRONTEND-AUDIT
 * §E): its role, who sponsors it, and its address — a twin has none
 * (ADR-0061). A popover rather than a hover card, because a hover card
 * never opens under a finger.
 */
export function AgentCard({
  name,
  kind,
  role,
  sponsor,
  principal,
  email,
  size = 28,
}: {
  name: string;
  kind: 'agent' | 'twin';
  role?: string | null;
  sponsor?: string | null;
  /** A twin's person. */
  principal?: string | null;
  email?: string | null;
  size?: number;
}) {
  const title = kind === 'twin' && principal ? twinLabel(name, principal) : `${name} Agent`;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" aria-label={`About ${title}`} className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]">
          <Avatar name={name} size={size} agent={{ label: title }} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          className="z-40 flex w-[min(300px,calc(100vw-32px))] flex-col gap-2 border border-strong bg-page p-4 shadow-toast outline-none"
        >
          <span className="text-dense font-semibold">{title}</span>
          {role && <span className="text-dense text-secondary">{role}</span>}
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-dense">
            {kind === 'twin' && principal && (
              <>
                <dt className="text-meta">Acts for</dt>
                <dd className="m-0">{principal}</dd>
              </>
            )}
            {sponsor && (
              <>
                <dt className="text-meta">Sponsor</dt>
                <dd className="m-0">{sponsor}</dd>
              </>
            )}
            <dt className="text-meta">Address</dt>
            <dd className="m-0 break-all">{kind === 'twin' ? 'None — a twin prepares, and never sends' : (email ?? '—')}</dd>
          </dl>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
